import { useState, useEffect, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { MessageCircle, BarChart3, Archive, Info, Share2, Bot } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import VentModal from './VentModal';
import VoteSection from './VoteSection';
import FeedSection from './FeedSection';
import TopSuggestionsLeaderboard from './TopSuggestionsLeaderboard';
import ShareModal from './ShareModal';
import NotificationToggle from './NotificationToggle';
import ThemeToggle from './ThemeToggle';
import VentLogo from './VentLogo';
import TopicOfTheWeek from './TopicOfTheWeek';
import TrendingKeywords from './TrendingKeywords';
import BadgesSection from './BadgesSection';
import CommunitiesRow from './CommunitiesRow';
import { analytics } from '../utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Non-critical error logger (avoids silent failures while keeping UI clean)
const logger = (context, err) => {
  if (process.env.NODE_ENV === 'development') {
    console.warn(`[Vent:${context}]`, err?.message || err);
  }
};

const DebatePage = ({ sessionId }) => {
  const navigate = useNavigate();
  const [topic, setTopic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userVote, setUserVote] = useState(null);
  const [hasVoted, setHasVoted] = useState(false);
  const [showVentModal, setShowVentModal] = useState(false);
  const [vents, setVents] = useState([]);
  const [voteAnimation, setVoteAnimation] = useState(null);
  const [sageImage, setSageImage] = useState(null);
  const [sageImageLoaded, setSageImageLoaded] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [feedFilter, setFeedFilter] = useState('all');
  const [topSuggestions, setTopSuggestions] = useState([]);

  const fetchSageImage = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/sage/image`);
      if (response.data.image_base64) {
        setSageImage(`data:image/png;base64,${response.data.image_base64}`);
      }
    } catch (err) { logger('fetchSageImage', err); }
  }, []);

  const fetchTodayTopic = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/topic/today`);
      setTopic(response.data);
    } catch (err) {
      logger('fetchTodayTopic', err);
      toast.error('Failed to load today\'s topic');
    } finally {
      setLoading(false);
    }
  }, []);

  const checkUserVote = useCallback(async () => {
    if (!topic) return;
    try {
      const response = await axios.get(`${API}/topic/${topic.id}/user-vote?session_id=${sessionId}`);
      setHasVoted(response.data.has_voted);
      setUserVote(response.data.vote_type);
    } catch (err) { logger('checkUserVote', err); }
  }, [topic, sessionId]);

  const fetchVents = useCallback(async () => {
    if (!topic) return;
    try {
      const response = await axios.get(`${API}/vents/${topic.id}`);
      setVents(response.data);
    } catch (err) { logger('fetchVents', err); }
  }, [topic]);

  const fetchTopSuggestions = useCallback(async () => {
    if (!topic) return;
    try {
      const response = await axios.get(`${API}/suggestions/top/${topic.id}?limit=3`);
      setTopSuggestions(response.data);
    } catch (err) { logger('fetchTopSuggestions', err); }
  }, [topic]);

  useEffect(() => {
    fetchTodayTopic();
    fetchSageImage();
  }, [fetchTodayTopic, fetchSageImage]);

  useEffect(() => {
    if (topic) {
      checkUserVote();
      fetchVents();
      fetchTopSuggestions();
    }
  }, [topic, checkUserVote, fetchVents, fetchTopSuggestions]);

  const filteredVents = useMemo(() => {
    if (feedFilter === 'vents') return vents.filter(v => !v.is_suggestion);
    if (feedFilter === 'suggestions') return vents.filter(v => v.is_suggestion);
    return vents;
  }, [vents, feedFilter]);

  const handleVote = useCallback(async (voteType) => {
    if (hasVoted) {
      toast.info('You have already voted on this topic');
      return;
    }
    try {
      setVoteAnimation(voteType);
      await axios.post(`${API}/topic/vote`, {
        topic_id: topic.id,
        vote_type: voteType,
        session_id: sessionId
      });
      setHasVoted(true);
      setUserVote(voteType);
      setTopic(prev => ({
        ...prev,
        votes_for: voteType === 'for' ? prev.votes_for + 1 : prev.votes_for,
        votes_against: voteType === 'against' ? prev.votes_against + 1 : prev.votes_against
      }));
      toast.success(voteType === 'for' ? 'You voted FOR!' : 'You voted AGAINST!');
      analytics.vote(voteType);
      setTimeout(() => setVoteAnimation(null), 1000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to vote');
      setVoteAnimation(null);
    }
  }, [hasVoted, topic, sessionId]);

  const handleVentSubmit = useCallback(async (content) => {
    try {
      const response = await axios.post(`${API}/vents`, {
        topic_id: topic.id,
        content,
        session_id: sessionId
      });
      setVents(prev => [response.data, ...prev]);
      setShowVentModal(false);
      const isSuggestion = response.data.is_suggestion;
      toast.success(
        isSuggestion
          ? 'Topic suggestion posted! Others can react to boost it.'
          : 'Your vent has been posted anonymously!'
      );
      analytics.vent(isSuggestion);
      if (isSuggestion) fetchTopSuggestions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post');
    }
  }, [topic, fetchTopSuggestions]);

  const totalVotes = (topic?.votes_for || 0) + (topic?.votes_against || 0);
  const forPercentage = totalVotes > 0 ? Math.round((topic?.votes_for / totalVotes) * 100) : 50;
  const againstPercentage = totalVotes > 0 ? Math.round((topic?.votes_against / totalVotes) * 100) : 50;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="w-12 h-12 border-2 border-vent-primary border-t-transparent rounded-full"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-vent-bg bg-pattern">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-40 glass">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <button
            onClick={() => {
              localStorage.removeItem('vent_splash_seen');
              localStorage.removeItem('vent_totw_seen');
              window.location.reload();
            }}
            className="cursor-pointer"
            data-testid="logo-restart-btn"
          >
            <VentLogo size="small" />
          </button>
          <div className="flex items-center gap-2 sm:gap-4">
            <ThemeToggle />
            <NotificationToggle />
            <button onClick={() => setShowShareModal(true)} className="p-2 hover:bg-white/10 rounded-lg transition-colors" data-testid="share-nav-btn" title="Share">
              <Share2 className="w-5 h-5 text-zinc-400 hover:text-vent-primary" />
            </button>
            <button onClick={() => navigate(`/results/${topic?.id}`)} className="p-2 hover:bg-white/10 rounded-lg transition-colors" data-testid="results-nav-btn" title="View Results">
              <BarChart3 className="w-5 h-5 text-zinc-400 hover:text-white" />
            </button>
            <button onClick={() => navigate('/archive')} className="p-2 hover:bg-white/10 rounded-lg transition-colors" data-testid="archive-nav-btn" title="Archive">
              <Archive className="w-5 h-5 text-zinc-400 hover:text-white" />
            </button>
            <button onClick={() => navigate('/preview-tomorrow')} className="p-2 hover:bg-white/10 rounded-lg transition-colors" data-testid="preview-tomorrow-nav-btn" title="Tomorrow's Topic (Agent)">
              <Bot className="w-5 h-5 text-zinc-400 hover:text-vent-secondary" />
            </button>
            <button onClick={() => navigate('/about')} className="p-2 hover:bg-white/10 rounded-lg transition-colors" data-testid="about-nav-btn" title="About">
              <Info className="w-5 h-5 text-zinc-400 hover:text-white" />
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-20 sm:pt-24 pb-44 sm:pb-32 px-4 sm:px-6">
        <div className="max-w-2xl mx-auto">
          {/* Sage Section */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="text-center mb-8 sm:mb-12">
            <motion.div animate={{ y: [0, -10, 0] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 0.3 }} className="relative w-24 h-24 sm:w-32 sm:h-32 mx-auto mb-4 sm:mb-6" data-testid="sage-image">
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: sageImageLoaded ? 1 : 0 }} transition={{ duration: 0.5 }} className="absolute inset-0 bg-vent-primary/20 rounded-full blur-2xl" />
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: sageImageLoaded ? 1 : 0 }} transition={{ duration: 0.5 }} className="absolute inset-1 bg-vent-secondary/10 rounded-full blur-xl" />
              {sageImage && (
                <motion.img src={sageImage} alt="The Universe Sage" className="relative w-full h-full object-cover rounded-full" initial={{ opacity: 0 }} animate={{ opacity: sageImageLoaded ? 1 : 0 }} transition={{ duration: 0.6 }} onLoad={() => setSageImageLoaded(true)} />
              )}
              {!sageImageLoaded && (
                <div className="relative w-full h-full rounded-full bg-gradient-to-br from-vent-primary/30 via-purple-600/30 to-vent-secondary/30 flex items-center justify-center">
                  <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }} className="w-8 h-8 border-2 border-vent-primary border-t-transparent rounded-full" />
                </div>
              )}
            </motion.div>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="text-zinc-400 text-base sm:text-lg leading-relaxed mb-6 sm:mb-8 font-body px-2" data-testid="sage-message">
              {topic?.sage_message}
            </motion.p>
            <p className="text-xs tracking-[0.2em] sm:tracking-[0.3em] uppercase text-zinc-600 mb-4">
              {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </motion.div>

          {/* Vote Section */}
          <VoteSection topic={topic} hasVoted={hasVoted} userVote={userVote} voteAnimation={voteAnimation} onVote={handleVote} />

          {/* Communities */}
          <CommunitiesRow />

          {/* VENT Button - Sticky on mobile */}
          <div className="fixed bottom-0 left-0 right-0 p-4 pb-24 sm:pb-4 sm:p-0 sm:relative sm:mt-6 bg-gradient-to-t from-black/90 via-black/60 to-transparent sm:from-transparent sm:via-transparent sm:bg-none z-50" style={{ paddingBottom: 'max(6rem, calc(env(safe-area-inset-bottom, 0px) + 5rem))' }}>
            <motion.button initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={() => setShowVentModal(true)} className="w-full btn-vent py-4 sm:py-5 rounded-full font-heading font-bold tracking-[0.15em] sm:tracking-[0.2em] text-base sm:text-lg flex items-center justify-center gap-2 sm:gap-3 shadow-lg" data-testid="vent-btn">
              <MessageCircle className="w-5 h-5" />
              VENT
            </motion.button>
          </div>

          {/* Community Section */}
          <TopicOfTheWeek />
          <TrendingKeywords topicId={topic?.id} />
          <TopSuggestionsLeaderboard topSuggestions={topSuggestions} />
          <FeedSection vents={vents} filteredVents={filteredVents} feedFilter={feedFilter} setFeedFilter={setFeedFilter} sessionId={sessionId} onUpdate={fetchVents} />

          <div className="mt-8 sm:mt-12">
            <BadgesSection sessionId={sessionId} />
          </div>
        </div>
      </main>

      <VentModal isOpen={showVentModal} onClose={() => setShowVentModal(false)} onSubmit={handleVentSubmit} />
      <ShareModal isOpen={showShareModal} onClose={() => setShowShareModal(false)} topic={topic} results={hasVoted ? { for_percentage: forPercentage, against_percentage: againstPercentage } : null} />
    </div>
  );
};

export default DebatePage;
